import React, { useEffect, useRef } from 'react';
import { Select, MenuItem, TextField } from '~/UIKit';
import { SelectChangeEvent } from '@mui/material';
import { connect, Field, FormikContextType, getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { DifferenceFormat } from '~/Models/DTO/Time.model';
import { makeStyles } from '~/UIKit/Style';
import { VERTICAL_SEPARATOR, FADED_ICON_ERROR, WHITE } from '~/Constants/Colors.constants';
import { ItemCreationToolTip, ItemCreationToolTipProps } from '~/Components';
import { EventGroupDefaultValue } from '~/Models/DTO/EventGroup.model';

interface CancellationWindowProps {
  isDisabled: boolean;
  hasAnError: boolean;
  name: string;
  label: string;
  type: string;
  tooltip?: Omit<ItemCreationToolTipProps, 'containerRef'> | false;
  max?: number;
  useOnlyHours?: boolean;
}

const useStyles = (hasAnError = false, isDisabled = false) =>
  makeStyles(
    (theme) => ({
      container: {
        display: 'flex',
        justifyContent: 'spaceBetween',
        paddingRight: 15,
        paddingLeft: 60,
        height: 50,
        alignItems: 'center',
        borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
        backgroundColor: hasAnError ? FADED_ICON_ERROR : WHITE,
        opacity: isDisabled ? 0.4 : 1,
      },
      label: {
        flexGrow: 1,
      },
      inputContainer: {
        display: 'flex',
        whiteSpace: 'normal',
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
          whiteSpace: 'nowrap',
        },
      },
      input: {
        marginRight: 10,
      },
      marginRight: {
        marginRight: 5,
      },
    }),
    { name: 'TimeSelector' },
  );

export const TimeSelector = connect<CancellationWindowProps, any>(
  ({
    formik,
    hasAnError,
    isDisabled,
    name: inputTimeName,
    label,
    type: inputTypeName,
    tooltip,
    max,
    useOnlyHours,
  }: CancellationWindowProps & { formik: FormikContextType<CancellationWindowProps> }) => {
    const MIN_INPUT = 0;
    const MAX_INPUT = 999;
    const { t } = useTranslation();
    const { classes } = useStyles(hasAnError, isDisabled)();
    const { HOURS, MINUTES } = DifferenceFormat;
    const containerRef = useRef(null);
    const numberInputValue = getIn(formik.values, inputTimeName);
    const typeInputValue = getIn(formik.values, inputTypeName);

    const options = [
      { label: t('app.time.hours'), value: HOURS },
      { label: t('app.time.minutes'), value: MINUTES },
    ];

    const onSelectChange = (event: SelectChangeEvent<number>) => {
      event.preventDefault();
      formik.setFieldValue(inputTypeName, Number(event.target.value));
    };

    const handleBlurNumbers = () => {
      if (!numberInputValue) {
        formik.setFieldValue(inputTimeName, 0);
      }
    };

    useEffect(() => {
      if (useOnlyHours) {
        formik.setFieldValue(inputTypeName, EventGroupDefaultValue.publicCancellationRefundEndType);
      }
    }, [useOnlyHours]);

    return (
      <div className={classes.container} ref={containerRef}>
        <div className={classes.label}>
          {label} <ItemCreationToolTip {...tooltip} containerRef={containerRef} />
        </div>
        <div className={classes.inputContainer}>
          <Field
            className={classes.input}
            newDesign
            component={TextField}
            fullWidth
            type="number"
            InputProps={{ inputProps: { min: MIN_INPUT, max: max || MAX_INPUT } }}
            disabled={isDisabled}
            onBlur={handleBlurNumbers}
            id={inputTimeName}
            name={inputTimeName}
            value={numberInputValue}
          />
          {useOnlyHours ? (
            <span className={classes.marginRight}>{t('app.time.hours')}</span>
          ) : (
            <Select
              newDesign
              onChange={onSelectChange}
              value={typeInputValue}
              disabled={isDisabled}
            >
              {options.map(({ label, value }) => (
                <MenuItem key={label} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          )}

          <span>{t('app.event.beforeEvent', { lowercase: true })}</span>
        </div>
      </div>
    );
  },
);
